// Live Meeting Room (LMR-6, Specs/Live Meeting Room — Spec, Oct 27 2026): the room's state as it
// fills in. The screen polls the operator API every few seconds for what's new after its
// cursors; this merges each answer in and decides which notes to show and who they're for.

export interface LiveLine { speaker: string; text: string; startSec?: number }
export interface LiveNote {
  n: number;
  type: "ask" | "gap" | "risk" | "fact" | "ctx" | "time" | "say" | "sop";
  label: string;
  forId: string | null;
  text: string;
  atLine: number;
  fromLine?: number; // sop notes: where the walkthrough started
  at?: string;
  lookedUp?: boolean;
  source?: string | null;
  question?: string;
}
export interface LiveLookup { id: string; question: string; forId: string | null }
export interface TeamMember { id: string; name: string }
export interface LiveTodo { n: number; task: string; owner: string | null; noteN: number | null; attachedId: string | null }
// OT-1/OT-3: a draft asked for from a note, made by the meeting's Paperclip agent
export interface LiveDraft { id: string; noteN: number; kind: "sop" | "draft"; status: "drafting" | "ready" | "failed"; name: string; error: string | null }
export interface LiveMeeting { id: string; title: string; status: string; startedAt?: string; endedAt?: string; minutes?: number }
export interface RoomResponse {
  meeting: LiveMeeting;
  lines: LiveLine[];
  cursor: number;
  notes: LiveNote[];
  notesCursor: number;
  lookups: LiveLookup[];
  team: TeamMember[];
  you: TeamMember | null;
  todos?: LiveTodo[];
  drafts?: LiveDraft[];
}
export interface RoomState {
  meeting: LiveMeeting | null;
  lines: LiveLine[];
  cursor: number;
  notes: LiveNote[];
  notesCursor: number;
  lookups: LiveLookup[];
  team: TeamMember[];
  you: TeamMember | null;
  todos: LiveTodo[];
  drafts: LiveDraft[];
}
export type NoteFilter = "all" | "me" | "team";

export const emptyRoom = (): RoomState => ({ meeting: null, lines: [], cursor: 0, notes: [], notesCursor: 0, lookups: [], team: [], you: null, todos: [], drafts: [] });

export const mergeRoom = (prev: RoomState, r: RoomResponse): RoomState => {
  const known = new Set(prev.notes.map((x) => x.n));
  return {
    meeting: r.meeting,
    lines: r.cursor > prev.cursor ? [...prev.lines, ...r.lines] : prev.lines,
    cursor: Math.max(prev.cursor, r.cursor),
    notes: [...prev.notes, ...r.notes.filter((x) => !known.has(x.n))],
    notesCursor: Math.max(prev.notesCursor, r.notesCursor),
    lookups: r.lookups || [],
    team: r.team || prev.team,
    you: r.you ?? prev.you,
    todos: r.todos ?? prev.todos,
    drafts: r.drafts ?? prev.drafts,
  };
};

export const roomIsLive = (s: RoomState) => s.meeting?.status === "live";

export const notesToShow = (notes: LiveNote[], filter: NoteFilter, youId: string | null) =>
  notes
    .filter((x) => (filter === "me" ? !!youId && x.forId === youId : filter === "team" ? x.forId === null : true))
    .slice()
    .reverse();

export const forLabel = (note: LiveNote, youId: string | null, team: TeamMember[]) => {
  const who = note.forId ? team.find((m) => m.id === note.forId) : null;
  if (!who) return "For the team";
  if (youId && who.id === youId) return "For you";
  return `For ${who.name.split(/\s+/)[0]}`;
};

// The server matches the signed-in email to the team; when it can't, fall back to the
// signed-in person's name (an account email that isn't on the team list).
export const whoIsViewing = (you: TeamMember | null, team: TeamMember[], user: { firstName?: string; lastName?: string } | null) => {
  if (you) return you.id;
  const first = String(user?.firstName || "").trim().toLowerCase();
  if (!first) return null;
  const full = `${first} ${String(user?.lastName || "").trim().toLowerCase()}`.trim();
  const hit = team.find((m) => m.name.toLowerCase() === full) || team.find((m) => m.name.split(/\s+/)[0].toLowerCase() === first);
  return hit ? hit.id : null;
};

// m:ss, or h:mm:ss past an hour ("2:03:16", not "123:16")
export const clock = (sec = 0) => {
  const s = Math.floor(sec % 60), m = Math.floor(sec / 60) % 60, h = Math.floor(sec / 3600);
  const ss = String(s).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
};

// the person a note is for, by name (the owner of a to-do made from it)
export const ownerOf = (note: LiveNote, team: TeamMember[]) => (note.forId && team.find((m) => m.id === note.forId)?.name) || null;

// An archived transcript (the brief's Transcript.md: "*Name:* text" or "**Name:** text" lines) read
// back into lines, so an archived meeting shows in the same view as a live one (Kyle, Oct 28).
// Text that isn't a transcript gives no lines, and the caller shows it as it was.
export const parseArchivedTranscript = (md: string): LiveLine[] => {
  const lines: LiveLine[] = [];
  for (const raw of String(md || "").split("\n")) {
    const row = raw.trim();
    if (!row) continue;
    const m = row.match(/^\*{1,2}([^*]+?):\*{1,2}\s*(.*)$/);
    if (m) lines.push({ speaker: m[1].trim(), text: m[2].trim() });
    else if (lines.length && !/^(#|>|---)/.test(row)) lines[lines.length - 1].text = `${lines[lines.length - 1].text} ${row}`.trim();
  }
  return lines;
};
